import {
  VESSEL_CARD_CP_CONSUMPTION_EXPONENT,
  VESSEL_CARD_CP_DELTA_BANDS,
  VESSEL_CARD_CP_DOUBLE_ABOUT_CONSUMPTION_FACTOR,
  VESSEL_CARD_CP_MARGINAL_DOUBLE_ABOUT_ALLOWANCE,
  VESSEL_CARD_CP_SPEED_ABOUT_ALLOWANCE,
} from "../constants/vessel-card.constants";
import type {
  VesselCardCpSource,
  VesselCardCpTerms,
  VesselCardCustomCpValues,
  VesselCardDashboard,
  VesselCardPerformanceRow,
} from "../models/vessel-card.models";

const getCustomCpTerms = (values: VesselCardCustomCpValues): VesselCardCpTerms[] => [
  { speed: values.ballastEcoSpeed, consumption: values.ballastEcoConsumption },
  { speed: values.ladenEcoSpeed, consumption: values.ladenEcoConsumption },
  { speed: values.ballastFullSpeed, consumption: values.ballastFullConsumption },
  { speed: values.ladenFullSpeed, consumption: values.ladenFullConsumption },
];

const unavailableComparison = (row: VesselCardPerformanceRow): VesselCardPerformanceRow => ({
  ...row,
  expectedAtCpSpeed: null,
  expectedAtCpConsumption: null,
  indicator: "Not available",
  indicatorTone: "neutral",
});

export const toVesselCardCustomCpValues = (rows: VesselCardPerformanceRow[]): VesselCardCustomCpValues => {
  const [ballastEco, ladenEco, ballastFull, ladenFull] = rows;
  return {
    ballastEcoSpeed: ballastEco?.cpSpeed ?? null,
    ballastEcoConsumption: ballastEco?.cpConsumption ?? null,
    ladenEcoSpeed: ladenEco?.cpSpeed ?? null,
    ladenEcoConsumption: ladenEco?.cpConsumption ?? null,
    ballastFullSpeed: ballastFull?.cpSpeed ?? null,
    ballastFullConsumption: ballastFull?.cpConsumption ?? null,
    ladenFullSpeed: ladenFull?.cpSpeed ?? null,
    ladenFullConsumption: ladenFull?.cpConsumption ?? null,
  };
};

// Selects CP terms independently of expected performance, then recalculates the current comparison.
// Athul's rule selects IFO solely from a confirmed IMOS scrubber; non-fitted or blank status selects LSF.
// The trader's comparison toggle and DVP performance basis do not change the vessel's CP fuel grade.
// Missing grade details from an older API stay unavailable rather than reviving the former combined fuel total.
// An absent IMOS Input source stays unavailable; its unresolved single-record selection rule is not guessed here.
export const getVesselCardCpPerformance = (
  card: VesselCardDashboard,
  source: VesselCardCpSource,
  customCp: VesselCardCustomCpValues,
): VesselCardPerformanceRow[] => {
  if (source === "imos") {
    const useIfo = card.scrubberInformation?.scrubberImosFitted === true;

    return card.performance.map((row) =>
      calculateVesselCardCpComparison({
        ...row,
        cpConsumption: (useIfo ? row.cpConsumptionByFuel?.ifo : row.cpConsumptionByFuel?.lsf) ?? null,
      }),
    );
  }

  if (source === "imosInput") {
    return card.performance.map((expected) => {
      const comparison = card.imosInputPerformance?.find((row) => row.mode === expected.mode);

      return calculateVesselCardCpComparison({
        ...expected,
        cpSpeed: comparison?.cpSpeed ?? null,
        cpConsumption: comparison?.cpConsumption ?? null,
      });
    });
  }

  const terms = getCustomCpTerms(customCp);
  return card.performance.map((row, index) =>
    calculateVesselCardCpComparison({
      ...row,
      cpSpeed: terms[index]?.speed ?? null,
      cpConsumption: terms[index]?.consumption ?? null,
    }),
  );
};
// === CP COMPARISON ===

// Recalculates all CP sources from the current expected values, including the HSFO-corrected Design consumption.
// Uses the supplied workbook equations rather than cached fixture results, so edited CP terms update immediately.
// Missing or invalid consumption never becomes zero, and No Data stays unavailable through every CP source.
export const calculateVesselCardCpComparison = (row: VesselCardPerformanceRow): VesselCardPerformanceRow => {
  const unavailable = unavailableComparison(row);

  if (
    !isAvailableCpMetric(row.expectedSpeed) ||
    !isAvailableCpMetric(row.expectedConsumption) ||
    !isAvailableCpMetric(row.cpConsumption)
  ) {
    return unavailable;
  }

  const expectedAtCpSpeed =
    row.expectedSpeed * Math.pow(row.cpConsumption / row.expectedConsumption, 1 / VESSEL_CARD_CP_CONSUMPTION_EXPONENT);

  if (!isAvailableCpMetric(expectedAtCpSpeed)) {
    return unavailable;
  }

  const calculated = { ...unavailable, expectedAtCpSpeed, expectedAtCpConsumption: row.cpConsumption };

  // CP speed is needed for the rating, but not for expected speed at a known CP consumption.
  if (!isAvailableCpMetric(row.cpSpeed)) {
    return calculated;
  }

  const delta = expectedAtCpSpeed - row.cpSpeed;
  const matchingBand = VESSEL_CARD_CP_DELTA_BANDS.find((band) => delta >= band.minimumDelta);

  if (matchingBand) {
    return { ...calculated, indicator: matchingBand.indicator, indicatorTone: matchingBand.indicatorTone };
  }

  const doubleAboutSpeed =
    (row.cpSpeed - VESSEL_CARD_CP_SPEED_ABOUT_ALLOWANCE) *
    Math.pow(1 / VESSEL_CARD_CP_DOUBLE_ABOUT_CONSUMPTION_FACTOR, 1 / VESSEL_CARD_CP_CONSUMPTION_EXPONENT);

  if (expectedAtCpSpeed >= doubleAboutSpeed) {
    return { ...calculated, indicator: "Within Double Abt", indicatorTone: "warning" };
  }

  if (expectedAtCpSpeed >= doubleAboutSpeed - VESSEL_CARD_CP_MARGINAL_DOUBLE_ABOUT_ALLOWANCE) {
    return { ...calculated, indicator: "Marginally Outside Double Abt", indicatorTone: "negative" };
  }

  return { ...calculated, indicator: "Outside Double Abt", indicatorTone: "negative" };
};

const isAvailableCpMetric = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && value > 0;
